import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Seo from "@/components/Seo";
import { useLenis } from "@/hooks/useLenis";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { Manifesto } from "@/components/landing/Manifesto";
import { Plans } from "@/components/landing/Plans";
import { Comparison } from "@/components/landing/Comparison";
import { DealerStrip } from "@/components/landing/DealerStrip";
import { Testimonials } from "@/components/landing/Testimonials";
import { Faq, FAQS } from "@/components/landing/Faq";
import { Footer } from "@/components/landing/Footer";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { MarketingScripts } from "@/components/landing/MarketingScripts";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider } from "@/content/ContentContext";
import { AdminApp } from "@/admin/AdminApp";
import StatePage from "@/pages/StatePage";
import BuyersGuide from "@/pages/BuyersGuide";
import CoverageIndex from "@/pages/CoverageIndex";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import Proforma from "@/pages/Proforma";
import { trackEvent } from "@/lib/analytics";

const Landing = () => {
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    useLenis();

    useEffect(() => {
        trackEvent("page_view");
    }, []);

    useEffect(() => {
        const lenis = window.__lenis;
        if (!lenis) return;
        if (checkoutOpen) lenis.stop();
        else lenis.start();
    }, [checkoutOpen]);

    const openCheckout = () => {
        trackEvent("quote_open");
        setCheckoutOpen(true);
    };

    return (
        <div className="min-h-screen bg-[#0A0D14] font-sans text-slate-50">
            <Seo
                title="AdvantaShield | Extended Car Warranty as a Monthly Subscription"
                siteName="AdvantaShield"
                description="The extended car warranty created by dealers, for dealers — accepted at dealership service counters and paid monthly. Cancel anytime."
                image="/og-image.png"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        name: "AdvantaShield",
                        description:
                            "Subscription-based vehicle service contracts created by dealers, for dealers — accepted at dealership service departments nationwide.",
                        logo: `${window.location.origin}/logo-stacked.png`,
                        telephone: "+1-888-687-4889",
                        contactPoint: {
                            "@type": "ContactPoint",
                            telephone: "+1-888-687-4889",
                            contactType: "customer service",
                            areaServed: "US",
                        },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "AdvantaShield",
                        url: window.location.origin,
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        serviceType: "Extended Vehicle Service Contract",
                        name: "AdvantaShield Subscription Vehicle Service Contract",
                        description:
                            "Subscription-priced extended car warranty accepted at dealership service departments and sold through dealers.",
                        provider: { "@type": "Organization", name: "AdvantaShield" },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: FAQS.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ]}
            />
            <MarketingScripts />
            <div className="grain-overlay" aria-hidden="true" />
            <Navbar onQuote={openCheckout} />
            <main>
                <Hero onQuote={openCheckout} scrollTo={(hash) => {
                    const el = document.querySelector(hash);
                    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
                }} />
                <Marquee />
                <Manifesto />
                <Plans onQuote={openCheckout} />
                <Comparison />
                <DealerStrip />
                <Testimonials />
                <Faq />
            </main>
            <Footer onQuote={openCheckout} />
            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
            <Toaster position="top-center" richColors />
        </div>
    );
};

function App() {
    return (
        <ContentProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/buyers-guide" element={<BuyersGuide />} />
                    <Route path="/coverage" element={<CoverageIndex />} />
                    <Route path="/coverage/:slug" element={<StatePage />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/proforma" element={<Proforma />} />
                    <Route path="/admin" element={<AdminApp />} />
                </Routes>
            </BrowserRouter>
        </ContentProvider>
    );
}

export default App;
